import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Plus, Minus, Trash2, MapPin, Wallet, CreditCard } from "lucide-react";
import { useCart } from "../context/CartContext";
import { LOCATIONS, BRAND } from "../data/site";
import { FoodTypeMark } from "../components/order/FoodTypeMark";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CheckoutPage() {
  const navigate = useNavigate();
  const {
    lines,
    count,
    subtotal,
    addItem,
    decrementItem,
    removeItem,
    storeId,
    clearCart,
  } = useCart();

  const store = useMemo(
    () => LOCATIONS.find((l) => l.id === storeId) || LOCATIONS[0],
    [storeId]
  );

  const [form, setForm] = useState({
    customer_name: "",
    phone: "",
    address: "",
    notes: "",
    payment_method: "cod",
  });
  const [placing, setPlacing] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const deliveryFee = subtotal >= 499 ? 0 : 39;
  const taxes = Math.round(subtotal * 0.05);
  const total = subtotal + deliveryFee + taxes;

  const placeOrder = async () => {
    if (count === 0) return toast.error("Your cart is empty.");
    if (!form.customer_name || !form.phone || !form.address) {
      return toast.error("Please fill in name, phone and address.");
    }
    setPlacing(true);
    try {
      const payload = {
        store_id: storeId || store.id,
        customer_name: form.customer_name,
        phone: form.phone,
        address: form.address,
        notes: form.notes || null,
        payment_method: form.payment_method,
        items: lines.map((l) => ({ item_id: l.id, qty: l.qty })),
      };
      const res = await axios.post(`${API}/orders`, payload);
      clearCart();
      toast.success("Order placed!");
      navigate(`/order-confirmed/${res.data.id}`, { replace: true });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail || "Could not place order. Try again."
      );
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="bg-[#F7F1E4] min-h-screen pb-24">
      <header className="sticky top-0 z-40 bg-[#FAF3E7]/95 backdrop-blur border-b border-[#E8D8C0]">
        <div className="max-w-5xl mx-auto px-4 md:px-8 h-16 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            data-testid="checkout-back"
            className="inline-flex items-center gap-1.5 text-cocoa-soft hover:text-cocoa text-sm"
          >
            <ArrowLeft size={16} /> Back to menu
          </button>
          <div className="flex items-center gap-2 ml-2">
            <img
              src={BRAND.logoTransparent}
              alt=""
              className="h-8 w-8 rounded-full ring-1 ring-[#C8A14B]/60 bg-[#3A1B0E]"
            />
            <span className="font-display text-cocoa">Checkout</span>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 md:px-8 pt-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left: Cart + Delivery details */}
        <div className="lg:col-span-3 space-y-5">
          <section className="bg-white border border-cream-deep rounded-2xl p-5 md:p-6">
            <h2 className="font-display text-xl text-cocoa">Your Cart</h2>
            <div className="text-xs text-cocoa-soft mt-1">
              From {store?.name} · {store?.area}
            </div>

            {count === 0 ? (
              <div className="mt-8 text-center text-cocoa-soft">
                Your cart is empty.{" "}
                <Link
                  to={`/order/${store.id}`}
                  className="text-gold font-semibold underline"
                >
                  Add some biryani →
                </Link>
              </div>
            ) : (
              <ul className="mt-5 divide-y divide-[#F0E2C8]">
                {lines.map((l) => (
                  <li
                    key={l.id}
                    data-testid={`cart-line-${l.id}`}
                    className="py-4 flex gap-4 items-center"
                  >
                    <img
                      src={l.image}
                      alt={l.name}
                      className="w-16 h-16 object-cover rounded-lg shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <FoodTypeMark type={l.type} />
                        <span className="text-sm font-semibold text-cocoa truncate">
                          {l.name}
                        </span>
                      </div>
                      <div className="text-xs text-cocoa-soft mt-1">
                        ₹{l.price} × {l.qty} = ₹{l.qty * l.price}
                      </div>
                    </div>
                    <div className="inline-flex items-center rounded-full bg-[#FAF3E7] border border-[#C8A14B] text-cocoa text-sm font-bold">
                      <button
                        onClick={() => decrementItem(l.id)}
                        data-testid={`cart-dec-${l.id}`}
                        className="px-2.5 py-1.5"
                        aria-label="Decrease"
                      >
                        <Minus size={12} />
                      </button>
                      <span className="px-2 min-w-[24px] text-center">
                        {l.qty}
                      </span>
                      <button
                        onClick={() =>
                          addItem(
                            { id: l.id, name: l.name, price: l.price, image: l.image, type: l.type },
                            storeId
                          )
                        }
                        data-testid={`cart-inc-${l.id}`}
                        className="px-2.5 py-1.5"
                        aria-label="Increase"
                      >
                        <Plus size={12} />
                      </button>
                    </div>
                    <button
                      onClick={() => removeItem(l.id)}
                      data-testid={`cart-remove-${l.id}`}
                      className="text-cocoa-soft hover:text-chilli p-2"
                      aria-label="Remove"
                    >
                      <Trash2 size={16} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {count > 0 && (
            <section className="bg-white border border-cream-deep rounded-2xl p-5 md:p-6">
              <h2 className="font-display text-xl text-cocoa">
                Delivery Details
              </h2>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-cocoa-soft font-semibold">
                    Full Name
                  </label>
                  <input
                    value={form.customer_name}
                    onChange={onChange("customer_name")}
                    data-testid="checkout-input-name"
                    type="text"
                    className="mt-1.5 w-full rounded-lg border border-cream-deep bg-white px-3 py-2.5 text-cocoa focus:outline-none focus:border-[#C8A14B] focus:ring-2 focus:ring-[#C8A14B]/30"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-cocoa-soft font-semibold">
                    Phone
                  </label>
                  <input
                    value={form.phone}
                    onChange={onChange("phone")}
                    data-testid="checkout-input-phone"
                    type="tel"
                    placeholder="+91 ..."
                    className="mt-1.5 w-full rounded-lg border border-cream-deep bg-white px-3 py-2.5 text-cocoa focus:outline-none focus:border-[#C8A14B] focus:ring-2 focus:ring-[#C8A14B]/30"
                    required
                  />
                </div>
              </div>
              <div className="mt-4">
                <label className="text-xs uppercase tracking-wider text-cocoa-soft font-semibold">
                  Delivery Address
                </label>
                <textarea
                  value={form.address}
                  onChange={onChange("address")}
                  data-testid="checkout-input-address"
                  rows={3}
                  className="mt-1.5 w-full rounded-lg border border-cream-deep bg-white px-3 py-2.5 text-cocoa focus:outline-none focus:border-[#C8A14B] focus:ring-2 focus:ring-[#C8A14B]/30 resize-none"
                  placeholder="House / flat number, street, landmark, city, pincode"
                  required
                />
              </div>
              <div className="mt-4">
                <label className="text-xs uppercase tracking-wider text-cocoa-soft font-semibold">
                  Order Notes (optional)
                </label>
                <input
                  value={form.notes}
                  onChange={onChange("notes")}
                  data-testid="checkout-input-notes"
                  type="text"
                  placeholder="Extra spicy, no onions, etc."
                  className="mt-1.5 w-full rounded-lg border border-cream-deep bg-white px-3 py-2.5 text-cocoa focus:outline-none focus:border-[#C8A14B] focus:ring-2 focus:ring-[#C8A14B]/30"
                />
              </div>

              <h3 className="font-display text-lg text-cocoa mt-6">
                Payment Method
              </h3>
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                <label
                  data-testid="checkout-payment-cod"
                  className={`cursor-pointer flex items-center gap-3 rounded-xl border px-4 py-3 transition-all ${
                    form.payment_method === "cod"
                      ? "border-[#C8A14B] bg-[#FFF8E6]"
                      : "border-cream-deep"
                  }`}
                >
                  <input
                    type="radio"
                    name="payment"
                    value="cod"
                    checked={form.payment_method === "cod"}
                    onChange={onChange("payment_method")}
                    className="sr-only"
                  />
                  <Wallet size={18} className="text-gold" />
                  <div className="leading-tight">
                    <div className="text-sm font-semibold text-cocoa">
                      Cash on Delivery
                    </div>
                    <div className="text-[11px] text-cocoa-soft">
                      Pay in cash when it arrives
                    </div>
                  </div>
                </label>
                <label
                  data-testid="checkout-payment-online"
                  className={`cursor-pointer flex items-center gap-3 rounded-xl border px-4 py-3 transition-all opacity-60`}
                  title="Coming soon"
                >
                  <input
                    type="radio"
                    name="payment"
                    value="online"
                    disabled
                    className="sr-only"
                  />
                  <CreditCard size={18} className="text-cocoa-soft" />
                  <div className="leading-tight">
                    <div className="text-sm font-semibold text-cocoa">
                      Online · Coming Soon
                    </div>
                    <div className="text-[11px] text-cocoa-soft">
                      UPI / Card / Wallet
                    </div>
                  </div>
                </label>
              </div>
            </section>
          )}
        </div>

        {/* Right: Order summary */}
        <aside className="lg:col-span-2">
          <div className="sticky top-24 bg-white border border-cream-deep rounded-2xl p-5 md:p-6">
            <h2 className="font-display text-xl text-cocoa">Bill Summary</h2>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-cocoa-soft">Item total</span>
                <span className="text-cocoa font-semibold">₹{subtotal}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-cocoa-soft">Delivery fee</span>
                <span className="text-cocoa font-semibold">
                  {deliveryFee === 0 ? (
                    <span className="text-[#2E7D32]">FREE</span>
                  ) : (
                    `₹${deliveryFee}`
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-cocoa-soft">Taxes & charges</span>
                <span className="text-cocoa font-semibold">₹{taxes}</span>
              </div>
            </div>
            <div className="mt-4 border-t border-[#E8D8C0] pt-4 flex justify-between items-baseline">
              <span className="font-display text-lg text-cocoa">To Pay</span>
              <span
                data-testid="checkout-total"
                className="font-display text-2xl text-cocoa"
              >
                ₹{total}
              </span>
            </div>

            <div className="mt-3 text-[11px] text-cocoa-soft flex items-start gap-1.5">
              <MapPin size={12} className="text-gold shrink-0 mt-0.5" />
              Delivering from <strong className="text-cocoa ml-1">{store?.name}</strong>, {store?.city}
            </div>

            <button
              onClick={placeOrder}
              disabled={placing || count === 0}
              data-testid="checkout-place-order-btn"
              className="mt-5 w-full rounded-full bg-[#C0392B] text-white hover:bg-[#D03A2A] disabled:opacity-60 disabled:cursor-not-allowed transition-all px-6 py-3.5 font-semibold shadow-md"
            >
              {placing ? "Placing Order…" : "Place Order"}
            </button>
            <p className="mt-3 text-[11px] text-cocoa-soft text-center">
              By placing the order, you agree to our terms of service.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
